import type { CSSProperties, FC, ReactNode } from 'react'
import type { Tone } from '@/types'

import { cn } from '@/lib/utils'
import { Cta } from '@/components/generals/cta'
import { SplitWords } from '@/components/generals/split-words'
import { TrialPoints } from '@/components/generals/trial-points'
import { LINKS } from '@/constants/site'

interface HeroQuote {
    quote: string
    name: string
    company: string
}

interface ProductHeroProps {
    eyebrow: string
    titleA: string
    titleB: string
    text: string
    visual: ReactNode
    tone?: Tone
    layout?: 'split' | 'stacked'
    quote?: HeroQuote
    primary?: { label: string, href: string }
    secondary?: { label: string, href: string } | null
    points?: readonly string[]
    id?: string
}

const DARK: Tone[] = ['night', 'deep', 'raised']

export const ProductHero: FC<ProductHeroProps> = ({
    eyebrow,
    titleA,
    titleB,
    text,
    visual,
    tone = 'deep',
    layout = 'split',
    quote,
    primary = { label: 'Prøv 14 dager gratis', href: LINKS.signup },
    secondary = { label: 'Bli oppringt', href: '/bli-oppringt' },
    points = ['Ingen kredittkort kreves', 'I gang på 2 minutter', 'Ingen auto-abonnement'],
    id = 'hero'
}): ReactNode => {
    const dark = DARK.includes(tone)
    const firstCount = titleA.split(' ').length

    const title = (
        <h1 className={cn(layout === 'stacked' ? 't-mega' : 't-display', 'balance')}>
            <span className='load-words block' style={{ '--load-delay': '0.15s' } as CSSProperties}>
                <SplitWords text={titleA} />
            </span>
            <span className='load-words t-serif block text-tone-ink' style={{ '--load-delay': '0.35s' } as CSSProperties}>
                <SplitWords text={titleB} start={firstCount} />
            </span>
        </h1>
    )

    const copy = (
        <div className='load-fade' style={{ '--load-delay': '0.8s' } as CSSProperties}>
            <p className='t-lead max-w-xl text-tone-muted pretty'>{text}</p>

            <div className='mt-8 flex flex-wrap gap-3'>
                <Cta href={primary.href} size='lg' magnetic>{primary.label}</Cta>
                {secondary && <Cta href={secondary.href} size='lg' variant='ghost'>{secondary.label}</Cta>}
            </div>

            <TrialPoints points={points} className='mt-7 text-tone-muted' />

            {quote && (
                <figure className='mt-10 max-w-md border-l-2 border-tone-accent pl-5'>
                    <blockquote className='text-[1.0625rem] leading-snug'>«{quote.quote}»</blockquote>
                    <figcaption className='mt-3 text-sm text-tone-muted'>
                        <span className='font-semibold text-tone-fg'>{quote.name}</span>, {quote.company}
                    </figcaption>
                </figure>
            )}
        </div>
    )

    return (
        <section
            id={id}
            className={cn(`tone-${tone}`, dark && 'grain', 'relative overflow-hidden')}
            data-header={dark ? 'dark' : 'light'}
        >
            <div className='grid-lines' />

            {layout === 'split' && (
                <div className='wrap relative grid min-h-[100svh] grid-cols-12 items-center gap-x-6 gap-y-14 pt-[calc(var(--header-h)+4rem)] pb-16 lg:pb-20'>
                    <div className='col-span-12 lg:col-span-6'>
                        <p className='t-label load-fade mb-8 text-tone-muted' style={{ '--load-delay': '0.05s' } as CSSProperties}>{eyebrow}</p>
                        {title}
                        <div className='mt-10'>{copy}</div>
                    </div>

                    <div className='col-span-12 lg:col-span-6'>
                        {visual}
                    </div>
                </div>
            )}

            {layout === 'stacked' && (
                <div className='wrap relative pt-[calc(var(--header-h)+4rem)] pb-16 lg:pb-24'>
                    <p className='t-label load-fade mb-8 text-tone-muted' style={{ '--load-delay': '0.05s' } as CSSProperties}>{eyebrow}</p>
                    {title}

                    <div className='mt-12 grid grid-cols-12 gap-x-6 gap-y-12'>
                        <div className='col-span-12 lg:col-span-5'>{copy}</div>
                        <div className='col-span-12 lg:col-span-7'>{visual}</div>
                    </div>
                </div>
            )}
        </section>
    )
}
